/*
  Journey Editor State Definition

  This module defines the Redux state shape for the journey editor,
  including the document model and API snapshots.
*/

import type {
  JourneyDocument,
  ApiJourneyVersionData,
  JourneyWorkflowMetadata,
} from '../types';
import type { Outcome } from '~/dashboard/views/settings/outcomes/types';

/**
 * Workflow reference data for journey editor
 */
export interface WorkflowData {
  application_token: string;
  name: string;
  type: string;
  active_public_version: number;
  archived: boolean;
  version_count: number;
}

/**
 * Journey action reference data
 */
export interface JourneyActionData {
  journey_action_id: number;
  journey_action_version_id: number;
  name: string;
  type: string;
  outcomes: string[]; // Just outcome names - customer_outcome_id looked up from props.outcomes
  version: string;
  archive_at: string | null;
  event_type_title?: string;
}

/**
 * Journey editor props - reference data used for building nodes
 */
export interface JourneyEditorProps {
  workflows: WorkflowData[];
  actions: JourneyActionData[];
  outcomes: Outcome[]; // All available outcomes with customer_outcome_id
}

/**
 * Journey Editor Redux State
 */
export interface JourneyEditorState {
  /** Current document model (normalized internal representation) */
  doc: JourneyDocument | null;

  /** Reference data for journey editor (workflows, actions, outcomes) */
  props: JourneyEditorProps | null;

  /** Unified editor metadata for coordinating workflow embedding */
  unified: {
    /** Currently expanded workflow token (null when no workflow is expanded) */
    expandedWorkflowToken: string | null;

    /** Bounds for rendering the expanded workflow canvas inside journey layout */
    expansionBounds: Record<string, ExpansionBounds>;

    /** Track which journey node is currently focused regardless of editor */
    focusedJourneyNodeId: string | null;

    /** Active editor context (journey or workflow token) */
    activeEditor: 'journey' | string;

    /** Track initialization state for each referenced workflow */
    workflows: Record<string, JourneyWorkflowMetadata>;
  };

  /** Last API snapshot for comparison and debugging */
  lastApiSnapshot: ApiJourneyVersionData | null;

  /** Error message if any operation fails */
  error: string | null;

  /** Current operation status */
  status: 'idle' | 'loading' | 'succeeded' | 'failed';

  /** Track if there are unsaved changes */
  hasUnsavedChanges: boolean;
}

/**
 * Initial state for the journey editor
 */
export const initialState: JourneyEditorState = {
  doc: null,
  props: null,
  unified: {
    expandedWorkflowToken: null,
    expansionBounds: {},
    focusedJourneyNodeId: null,
    activeEditor: 'journey',
    workflows: {},
  },
  lastApiSnapshot: null,
  error: null,
  status: 'idle',
  hasUnsavedChanges: false,
};

export interface ExpansionBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}
